import {
  $addUpdateTag,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $selectAll,
  $setSelection,
  FORMAT_TEXT_COMMAND,
  SKIP_DOM_SELECTION_TAG,
  type LexicalEditor,
} from "lexical";

/**
 * Runs `dispatch` with a usable selection in the editor.
 *
 * Canvas toolbars act on a node that is merely selected as often as on one
 * being edited, and in that state the editor has either no selection at all
 * or a stale collapsed one left over from the last edit — a command dispatched
 * into it does nothing. `borrow` supplies the range to act on instead, and it
 * is dropped again afterwards; nothing is ever reconciled to the DOM, so an
 * unfocused editor neither takes focus nor paints a highlight.
 */
export function withCanvasSelection(
  editor: LexicalEditor,
  borrow: () => void,
  dispatch: () => void,
): void {
  const rootEl = editor.getRootElement();
  const isFocused = !!rootEl && rootEl.contains(document.activeElement);
  let borrowed = false;

  editor.update(() => {
    const selection = $getSelection();
    if (
      $isRangeSelection(selection) &&
      (isFocused || !selection.isCollapsed())
    ) {
      return;
    }
    $addUpdateTag(SKIP_DOM_SELECTION_TAG);
    borrow();
    borrowed = true;
  });

  dispatch();

  if (borrowed) {
    editor.update(() => {
      // A focused editor keeps a real caret (at the end, where typing
      // continues). Leaving it with no selection strands the next keystroke:
      // the browser caret stays before the first character and every later
      // one lands ahead of it.
      if (isFocused) {
        $getRoot().selectEnd();
        return;
      }
      $addUpdateTag(SKIP_DOM_SELECTION_TAG);
      $setSelection(null);
    });
  }
}

/**
 * Toggles strikethrough on a canvas text surface. Bold, italic and underline
 * live on the node and apply to the whole label or note; strikethrough lives
 * in the content, so it borrows the whole text to match their reach.
 */
export function toggleCanvasStrikethrough(editor: LexicalEditor | null): void {
  if (!editor) return;
  withCanvasSelection(
    editor,
    () => $selectAll(),
    () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough"),
  );
}

export type CanvasAlignment = "left" | "center" | "right";

/**
 * The alignment the content actually carries. A merely selected node has no
 * caret, so nothing fires the editor's update listener and a toolbar that
 * starts at "left" keeps claiming left; this reads the row instead — the
 * caret's when there is one, the first row's otherwise.
 */
export function readCanvasAlignment(
  editor: LexicalEditor | null,
): CanvasAlignment {
  if (!editor) return "left";
  let alignment: CanvasAlignment = "left";
  editor.getEditorState().read(() => {
    const selection = $getSelection();
    const node = $isRangeSelection(selection)
      ? selection.anchor.getNode()
      : $getRoot().getFirstChild();
    if (!node) return;
    const element =
      node.getKey() === "root" ? node : node.getTopLevelElementOrThrow();
    if (!$isElementNode(element)) return;
    const format = element.getFormatType();
    if (format === "center" || format === "right") alignment = format;
  });
  return alignment;
}

/** Puts the caret at the start of the first row — the row a list acts on. */
export function selectFirstRow(): void {
  const first = $getRoot().getFirstChild();
  if (first && "selectStart" in first) {
    (first as { selectStart: () => void }).selectStart();
    return;
  }
  $getRoot().selectStart();
}
